import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthPrincipal } from '@mg/shared';
import { configureApi } from '../lib/api';
import { devAuthEnabled } from '../lib/env';

/**
 * Auth state for the stakeholder approval portal. Independent of the agency
 * AuthProvider: stakeholders authenticate via magic-link (email + 6-digit code),
 * receive a bearer token, and are scoped to a single tenant.
 *
 * In dev, a bypass lets you view the portal shell without a code.
 */
interface PortalContextValue {
  principal: AuthPrincipal | null;
  tenantSlug: string | null;
  isAuthed: boolean;
  devBypass: boolean;
  signIn: (token: string, principal: AuthPrincipal, tenantSlug: string | null) => void;
  enableDevBypass: () => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const tokenRef = useRef<string | null>(null);
  const tenantRef = useRef<string | null>(null);
  const [principal, setPrincipal] = useState<AuthPrincipal | null>(null);
  const [devBypass, setDevBypass] = useState(false);

  // Point the shared API client at the portal's token/tenant.
  const wire = useCallback(() => {
    configureApi({
      token: async () => tokenRef.current,
      tenant: () => tenantRef.current,
    });
  }, []);

  const signIn = useCallback(
    (token: string, p: AuthPrincipal, tenantSlug: string | null) => {
      tokenRef.current = token;
      tenantRef.current = tenantSlug;
      wire();
      setPrincipal(p);
    },
    [wire],
  );

  const enableDevBypass = useCallback(() => {
    // In dev the API's DEV_AUTH injects a principal even without a token.
    tokenRef.current = null;
    tenantRef.current = null;
    wire();
    setDevBypass(true);
  }, [wire]);

  const value = useMemo<PortalContextValue>(
    () => ({
      principal,
      tenantSlug: tenantRef.current,
      isAuthed: principal !== null || devBypass,
      devBypass: devAuthEnabled && devBypass,
      signIn,
      enableDevBypass,
    }),
    [principal, devBypass, signIn, enableDevBypass],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used within <PortalProvider>');
  return ctx;
}
